<template>
  <div class="min-h-screen bg-[#F8FAFC] flex flex-col font-sans text-slate-900 selection:bg-indigo-100">
    
    <header class="bg-white border-b border-slate-200/60 px-8 py-5 flex md:flex-row justify-between items-start md:items-center gap-4 z-20">
      <div class="flex items-center gap-4">
        <div class="w-12 h-12 bg-indigo-600 rounded-2xl flex items-center justify-center text-white shadow-lg shadow-indigo-200">
          <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
        </div>
        <div>
          <h1 class="text-2xl font-black tracking-tight text-slate-900">Médiathèque</h1>
          <p class="text-xs font-bold text-slate-400 uppercase tracking-widest">Gestionnaire d'assets cloud</p>
        </div>
      </div>

      <div class="flex items-center gap-3 md:w-auto">
        <button 
          @click="fetchMedia(); fetchFolders();" 
          class="p-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-400 hover:text-indigo-600 hover:bg-white transition-all active:scale-95"
          title="Actualiser"
        >
          <svg class="w-5 h-5" :class="{ 'animate-spin': loading }" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
        </button>
        <button 
          @click="showUploadModal = true"
          class="flex-1 md:flex-none flex items-center justify-center gap-2 bg-slate-900 text-white px-6 py-3 rounded-xl font-black text-xs uppercase tracking-widest hover:bg-slate-800 shadow-xl shadow-slate-200 transition-all active:scale-95"
        >
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-width="3" d="M12 4v16m8-8H4" /></svg>
          Upload Assets
        </button>
      </div>
    </header>

    <div class="flex flex-1 overflow-hidden">
      
      <aside class="w-72 bg-white border-r border-slate-200/60 hidden lg:flex flex-col p-6 overflow-y-auto">
        <div class="mb-8">
          <h3 class="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mb-4">Structure Dossiers</h3>
          <nav class="space-y-1">
            <button
              @click="selectFolder('all')"
              :class="selectedFolder === 'all' ? 'bg-indigo-50 text-indigo-600 ring-1 ring-indigo-100' : 'text-slate-500 hover:bg-slate-50'"
              class="w-full flex items-center justify-between px-4 py-3 rounded-xl transition-all group"
            >
              <div class="flex items-center gap-3">
                <svg class="w-5 h-5 opacity-70" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-width="2" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" /></svg>
                <span class="text-sm font-bold">Tous les fichiers</span>
              </div>
              <span class="text-[10px] font-black opacity-40 tabular-nums">{{ totalCount }}</span>
            </button>

            <button
              v-for="folder in folders"
              :key="folder.name"
              @click="selectFolder(folder.name)"
              :class="selectedFolder === folder.name ? 'bg-indigo-50 text-indigo-600 ring-1 ring-indigo-100' : 'text-slate-500 hover:bg-slate-50'"
              class="w-full flex items-center justify-between px-4 py-3 rounded-xl transition-all"
            >
              <div class="flex items-center gap-3">
                <svg class="w-5 h-5 opacity-70" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-width="2" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" /></svg>
                <span class="text-sm font-bold truncate">{{ folder.name }}</span>
              </div>
              <span class="text-[10px] font-black opacity-40 tabular-nums">{{ folder.count }}</span>
            </button>
          </nav>
        </div>

        <div class="mt-auto pt-6 border-t border-slate-100">
           <div class="bg-slate-50 rounded-2xl p-4">
              <p class="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Utilisation Cloud</p>
              <div class="h-1.5 w-full bg-slate-200 rounded-full overflow-hidden mb-2">
                <div class="h-full bg-indigo-500 w-[65%]"></div>
              </div>
              <p class="text-[10px] font-bold text-slate-500 text-right">6.5 GB / 10 GB</p>
           </div>
        </div>
      </aside>

      <main class="flex-1 flex flex-col min-w-0 bg-[#F8FAFC]">
        
        <div class="px-8 py-4 bg-white/50 backdrop-blur-sm border-b border-slate-200/60 flex flex-wrap items-center justify-between gap-4">
          <div class="flex-1 min-w-[300px] relative">
            <span class="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
            </span>
            <input 
              v-model="searchQuery" 
              type="text" 
              placeholder="Filtrer par nom, tag ou format..." 
              class="w-full pl-10 pr-4 py-2.5 bg-white border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-100 focus:border-indigo-400 transition-all text-sm font-medium"
              @input="debounceSearch"
            >
          </div>

          <div class="flex items-center gap-4">
            <div class="flex bg-slate-100 p-1 rounded-xl">
              <button @click="viewMode = 'grid'" :class="viewMode === 'grid' ? 'bg-white shadow-sm text-indigo-600' : 'text-slate-400'" class="p-2 rounded-lg transition-all">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-width="2" d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" /></svg>
              </button>
              <button @click="viewMode = 'list'" :class="viewMode === 'list' ? 'bg-white shadow-sm text-indigo-600' : 'text-slate-400'" class="p-2 rounded-lg transition-all">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-width="2" d="M4 6h16M4 12h16M4 18h16" /></svg>
              </button>
            </div>
            
            <button 
              v-if="selectedImages.length > 0"
              @click="deleteSelected"
              class="flex items-center gap-2 bg-rose-50 text-rose-600 px-4 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest border border-rose-100 hover:bg-rose-100 transition-all"
            >
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-4v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
              Supprimer ({{ selectedImages.length }})
            </button>
          </div>
        </div>

        <div class="flex-1 overflow-y-auto p-8 custom-scrollbar">
          
          <div v-if="loading" class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-6 animate-pulse">
            <div v-for="n in 12" :key="n" class="aspect-square bg-slate-200 rounded-[2rem]"></div>
          </div>

          <div v-else-if="media.length > 0" :class="viewMode === 'grid' ? 'grid md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-6' : 'space-y-2'">
            <div
              v-for="item in media"
              :key="item._id"
              @click="toggleSelect(item._id, $event)"
              :class="[
                'group relative bg-white border transition-all duration-300 cursor-pointer overflow-hidden',
                viewMode === 'grid' ? 'rounded-[2rem] aspect-square flex flex-col' : 'rounded-xl flex items-center p-3 gap-4',
                selectedImages.includes(item._id) ? 'ring-2 ring-indigo-500 border-transparent shadow-lg shadow-indigo-100' : 'border-slate-200/60 hover:border-indigo-300 shadow-sm hover:shadow-md'
              ]"
            >
              <div :class="viewMode === 'grid' ? 'flex-1' : 'w-12 h-12 rounded-lg'" class="relative overflow-hidden bg-slate-100">
                <img :src="item.url" :alt="item.name" class="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                
                <div v-if="selectedImages.includes(item._id)" class="absolute inset-0 bg-indigo-600/20 backdrop-blur-[2px] flex items-center justify-center">
                  <div class="w-8 h-8 bg-indigo-600 text-white rounded-full flex items-center justify-center shadow-lg animate-scale-in">
                    <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-width="3" d="M5 13l4 4L19 7" /></svg>
                  </div>
                </div>

                <div class="absolute top-3 right-3 flex flex-col gap-2 translate-x-12 opacity-0 group-hover:translate-x-0 group-hover:opacity-100 transition-all duration-300">
                   <button @click.stop="copyUrl(item.url)" class="p-2 bg-white/90 backdrop-blur shadow-sm rounded-lg text-slate-600 hover:text-indigo-600 transition-colors">
                     <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-width="2" d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" /></svg>
                   </button>
                   <button @click.stop="editMedia(item)" class="p-2 bg-white/90 backdrop-blur shadow-sm rounded-lg text-slate-600 hover:text-emerald-600 transition-colors">
                     <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                   </button>
                </div>
              </div>

              <div :class="viewMode === 'grid' ? 'p-4 pt-2' : 'flex-1'">
                <p class="text-[11px] font-black text-slate-900 truncate uppercase tracking-tighter">{{ item.name }}</p>
                <div class="flex items-center justify-between mt-1">
                  <p class="text-[9px] font-bold text-slate-400 uppercase tabular-nums">
                    {{ item.metadata?.width }}×{{ item.metadata?.height }} • {{ item.readableSize }}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div v-else class="h-full flex flex-col items-center justify-center p-12 text-center animate-fade-in-down">
            <div class="w-32 h-32 bg-slate-100 rounded-[3rem] flex items-center justify-center mb-6">
              <svg class="w-16 h-16 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-width="1" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
            </div>
            <h3 class="text-xl font-black text-slate-900 mb-2">Médiathèque vide</h3>
            <p class="text-slate-500 font-medium max-w-sm mb-8 text-sm">Organisez vos contenus visuels simplement. Commencez par uploader vos premiers assets.</p>
            <button @click="showUploadModal = true" class="bg-indigo-600 text-white px-8 py-3 rounded-2xl font-black text-xs uppercase tracking-[0.2em] shadow-xl shadow-indigo-100 hover:-translate-y-1 transition-all active:scale-95">
              Charger des Fichiers
            </button>
          </div>
        </div>

        <footer v-if="pagination.pages > 1" class="px-8 py-4 bg-white border-t border-slate-100 flex items-center justify-between">
            <span class="text-[10px] font-black text-slate-400 uppercase tracking-widest">
              Fichiers {{ (pagination.page - 1) * pagination.limit + 1 }} - {{ Math.min(pagination.page * pagination.limit, totalCount) }} sur {{ totalCount }}
            </span>
            <div class="flex gap-2">
              <button @click="changePage(pagination.page - 1)" :disabled="pagination.page === 1" class="px-4 py-2 rounded-xl border border-slate-200 text-[10px] font-black uppercase tracking-widest disabled:opacity-30 hover:bg-slate-50 transition-all">Précédent</button>
              <button @click="changePage(pagination.page + 1)" :disabled="pagination.page === pagination.pages" class="px-4 py-2 rounded-xl bg-indigo-50 text-indigo-600 text-[10px] font-black uppercase tracking-widest disabled:opacity-30 hover:bg-indigo-100 transition-all">Suivant</button>
            </div>
        </footer>
      </main>
    </div>

    <Transition name="fade">
      <div v-if="showUploadModal" class="fixed inset-0 bg-slate-900/60 backdrop-blur-md z-50 flex items-center justify-center p-4">
        <div class="bg-white rounded-[2.5rem] shadow-2xl w-full max-w-xl p-8 animate-scale-in border border-white/20">
          <div class="flex justify-between items-center mb-8">
            <h3 class="text-2xl font-black text-slate-900 tracking-tight">Importer des Médias</h3>
            <button @click="showUploadModal = false" class="text-slate-400 hover:text-slate-600 transition-colors">
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-width="2.5" d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
          </div>

          <div
            class="group relative border-2 border-dashed border-slate-200 rounded-[2rem] p-12 text-center transition-all cursor-pointer hover:border-indigo-400 hover:bg-indigo-50/50"
            :class="{ 'border-indigo-500 bg-indigo-50': isDragging }"
            @drop.prevent="handleDrop"
            @dragover.prevent="isDragging = true"
            @dragleave="isDragging = false"
            @click="$refs.fileInput.click()"
          >
            <input ref="fileInput" type="file" accept="image/*" multiple @change="handleFileSelect" class="hidden" />
            <div class="w-20 h-20 bg-white shadow-md rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
              <svg class="w-10 h-10 text-indigo-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-width="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" /></svg>
            </div>
            <p class="text-slate-900 font-bold mb-1">Glissez-déposez vos fichiers</p>
            <p class="text-slate-400 text-xs font-medium uppercase tracking-widest">Formats supportés : JPG, PNG, WEBP, SVG</p>
          </div>

          <div class="mt-8 space-y-6">
            <div class="flex items-center gap-4 bg-slate-50 p-4 rounded-2xl">
              <div class="p-2 bg-white rounded-lg shadow-sm">
                <svg class="w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-width="2" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" /></svg>
              </div>
              <div class="flex-1">
                <label class="block text-[10px] font-black text-slate-400 uppercase tracking-widest">Dossier de destination</label>
                <input v-model="uploadFolder" type="text" class="w-full bg-transparent border-none p-0 focus:ring-0 text-sm font-bold text-slate-900" placeholder="root" />
              </div>
            </div>

            <div v-if="uploading" class="space-y-2">
              <div class="flex justify-between text-[10px] font-black uppercase text-slate-500">
                <span>Envoi en cours...</span>
                <span>{{ uploadProgress }}%</span>
              </div>
              <div class="h-2 w-full bg-slate-100 rounded-full overflow-hidden">
                <div class="h-full bg-indigo-500 transition-all duration-300" :style="{ width: uploadProgress + '%' }"></div>
              </div>
            </div>
          </div>

          <div class="mt-8 grid grid-cols-2 gap-4">
            <button @click="showUploadModal = false" class="py-4 rounded-2xl border-2 border-slate-100 text-slate-400 font-black text-xs uppercase tracking-widest hover:bg-slate-50 transition-all">Fermer</button>
            <button @click="$refs.fileInput.click()" class="py-4 rounded-2xl bg-indigo-600 text-white font-black text-xs uppercase tracking-widest hover:bg-indigo-700 shadow-xl shadow-indigo-100 transition-all active:scale-95">Parcourir</button>
          </div>
        </div>
      </div>
    </Transition>

    <Transition name="fade">
      <div v-if="editingMedia" class="fixed inset-0 bg-slate-900/60 backdrop-blur-md z-50 flex items-center justify-center p-4">
        <div class="bg-white rounded-[2.5rem] shadow-2xl w-full max-w-lg p-10 animate-scale-in border border-white/20">
          <div class="flex items-center gap-6 mb-8 pb-8 border-b border-slate-100">
            <div class="w-24 h-24 rounded-2xl overflow-hidden bg-slate-100 shadow-sm">
              <img :src="editingMedia.url" class="w-full h-full object-cover" />
            </div>
            <div>
              <h3 class="text-xl font-black text-slate-900 tracking-tight">Détails du fichier</h3>
              <p class="text-xs font-bold text-slate-400 uppercase tracking-widest truncate max-w-[200px]">{{ editingMedia.name }}</p>
            </div>
          </div>

          <div class="space-y-6">
            <div>
              <label class="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 px-1">Nom d'affichage</label>
              <input v-model="editForm.name" type="text" class="w-full px-5 py-3.5 bg-slate-50 border-2 border-slate-100 rounded-2xl focus:ring-0 focus:border-indigo-500 transition-all font-bold text-slate-800" />
            </div>
            <div class="grid grid-cols-2 gap-4">
              <div>
                <label class="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 px-1">Dossier</label>
                <input v-model="editForm.folder" type="text" class="w-full px-5 py-3.5 bg-slate-50 border-2 border-slate-100 rounded-2xl focus:ring-0 focus:border-indigo-500 transition-all font-bold text-slate-800" />
              </div>
              <div>
                <label class="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 px-1">Tags</label>
                <input v-model="editForm.tags" type="text" placeholder="logo, header..." class="w-full px-5 py-3.5 bg-slate-50 border-2 border-slate-100 rounded-2xl focus:ring-0 focus:border-indigo-500 transition-all font-bold text-slate-800" />
              </div>
            </div>
            <div>
              <label class="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 px-1">Description</label>
              <textarea v-model="editForm.description" rows="3" class="w-full px-5 py-3.5 bg-slate-50 border-2 border-slate-100 rounded-2xl focus:ring-0 focus:border-indigo-500 transition-all font-bold text-slate-800"></textarea>
            </div>
          </div>

          <div class="mt-10 grid grid-cols-2 gap-4">
            <button @click="editingMedia = null" class="py-4 rounded-2xl border-2 border-slate-100 text-slate-400 font-black text-xs uppercase tracking-widest hover:bg-slate-50 transition-all">Annuler</button>
            <button @click="saveEdit" class="py-4 rounded-2xl bg-indigo-600 text-white font-black text-xs uppercase tracking-widest hover:bg-indigo-700 shadow-xl shadow-indigo-100 transition-all active:scale-95">Enregistrer</button>
          </div>
          <button @click="deleteMedia(editingMedia._id)" class="w-full mt-4 text-[10px] font-black text-rose-400 uppercase tracking-widest hover:text-rose-600 transition-colors">Supprimer définitivement l'asset</button>
        </div>
      </div>
    </Transition>

  </div>
</template>

<script setup>
import { ref, onMounted, } from 'vue';
import { toast } from 'vue3-toastify';
import 'vue3-toastify/dist/index.css';

/**
 * LOGIQUE ET ÉTAT
 */
const API_BASE = 'https://api.datafuse.fr/api/super-admin/media';

const loading = ref(false);
const media = ref([]);
const folders = ref([]);
const selectedFolder = ref('all');
const searchQuery = ref('');
const viewMode = ref('grid');
const selectedImages = ref([]);
const totalCount = ref(0);
const pagination = ref({ page: 1, pages: 1, total: 0, limit: 24 });

// Upload
const showUploadModal = ref(false);
const uploadFolder = ref('root');
const uploading = ref(false);
const uploadProgress = ref(0);
const isDragging = ref(false);
const fileInput = ref(null);

// Edit
const editingMedia = ref(null);
const editForm = ref({ name: '', description: '', folder: '', tags: '' });

/**
 * ACTIONS API
 */
const fetchMedia = async () => {
  loading.value = true;
  try {
    const token = localStorage.getItem('superAdminToken');
    const params = new URLSearchParams({
      page: pagination.value.page,
      limit: pagination.value.limit,
      folder: selectedFolder.value,
      ...(searchQuery.value && { search: searchQuery.value })
    });

    const res = await fetch(`${API_BASE}/list?${params}`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });

    if (res.ok) {
      const data = await res.json();
      media.value = data.media;
      pagination.value = data.pagination;
      totalCount.value = data.pagination.total;
    }
  } catch (err) {
    toast.error('Échec du chargement des fichiers');
  } finally {
    setTimeout(() => { loading.value = false; }, 400);
  }
};

const fetchFolders = async () => {
  try {
    const token = localStorage.getItem('superAdminToken');
    const res = await fetch(`${API_BASE}/folders`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    if (res.ok) {
      const data = await res.json();
      folders.value = data.folders;
    }
  } catch (err) {
    console.error(err);
  }
};

const selectFolder = (folder) => {
  selectedFolder.value = folder;
  pagination.value.page = 1;
  fetchMedia();
};

let searchTimeout;
const debounceSearch = () => {
  clearTimeout(searchTimeout);
  searchTimeout = setTimeout(() => {
    pagination.value.page = 1;
    fetchMedia();
  }, 500);
};

/**
 * GESTION DES FICHIERS (Upload & Select)
 */
const toggleSelect = (id, event) => {
  if (event.ctrlKey || event.metaKey) {
    const index = selectedImages.value.indexOf(id);
    index > -1 ? selectedImages.value.splice(index, 1) : selectedImages.value.push(id);
  } else {
    selectedImages.value = [id];
  }
};

const handleFileSelect = (e) => uploadFiles(e.target.files);
const handleDrop = (e) => {
  isDragging.value = false;
  uploadFiles(e.dataTransfer.files);
};

const uploadFiles = async (files) => {
  if (!files.length) return;
  uploading.value = true;
  uploadProgress.value = 0;

  try {
    const token = localStorage.getItem('superAdminToken');
    const formData = new FormData();
    formData.append('folder', uploadFolder.value || 'root');
    for (let file of files) { formData.append('images', file); }

    const xhr = new XMLHttpRequest();
    xhr.upload.addEventListener('progress', (e) => {
      if (e.lengthComputable) uploadProgress.value = Math.round((e.loaded / e.total) * 100);
    });

    xhr.addEventListener('load', () => {
      if (xhr.status === 201 || xhr.status === 200) {
        toast.success('Sync réussie !');
        showUploadModal.value = false;
        fetchMedia();
        fetchFolders();
      } else {
        toast.error('Erreur lors de l\'upload');
      }
      uploading.value = false;
    });

    xhr.open('POST', `${API_BASE}/upload-batch`);
    if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    xhr.send(formData);
  } catch (err) {
    toast.error('Erreur réseau');
    uploading.value = false;
  }
};

/**
 * MODIFICATION & SUPPRESSION
 */
const editMedia = (item) => {
  editingMedia.value = item;
  editForm.value = {
    name: item.name,
    description: item.description || '',
    folder: item.folder,
    tags: item.tags?.join(', ') || ''
  };
};

const saveEdit = async () => {
  try {
    const token = localStorage.getItem('superAdminToken');
    const res = await fetch(`${API_BASE}/${editingMedia.value._id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(editForm.value)
    });

    if (res.ok) {
      toast.success('Fichier mis à jour');
      editingMedia.value = null;
      fetchMedia();
      fetchFolders();
    }
  } catch (err) {
    toast.error('Erreur de sauvegarde');
  }
};

const deleteMedia = async (id) => {
  if (!confirm('Voulez-vous supprimer cet asset ?')) return;
  try {
    const token = localStorage.getItem('superAdminToken');
    const res = await fetch(`${API_BASE}/${id}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    if (res.ok) {
      toast.success('Média supprimé');
      editingMedia.value = null;
      fetchMedia();
      fetchFolders();
    }
  } catch (err) {
    toast.error('Suppression impossible');
  }
};

const deleteSelected = async () => {
  if (!confirm(`Supprimer ces ${selectedImages.value.length} fichiers ?`)) return;
  try {
    const token = localStorage.getItem('superAdminToken');
    const res = await fetch(`${API_BASE}/delete-batch`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ ids: selectedImages.value })
    });
    if (res.ok) {
      toast.success('Assets supprimés');
      selectedImages.value = [];
      fetchMedia();
      fetchFolders();
    }
  } catch (err) {
    toast.error('Erreur de suppression par lot');
  }
};

const copyUrl = (url) => {
  navigator.clipboard.writeText(url);
  toast.success('Lien copié dans le presse-papier', { autoClose: 1500 });
};

const changePage = (page) => {
  pagination.value.page = page;
  fetchMedia();
};

/**
 * LIFECYCLE
 */
onMounted(() => {
  fetchMedia();
  fetchFolders();
});
</script>

<style scoped>
.custom-scrollbar::-webkit-scrollbar {
  width: 6px;
}
.custom-scrollbar::-webkit-scrollbar-track {
  background: transparent;
}
.custom-scrollbar::-webkit-scrollbar-thumb {
  background: #E2E8F0;
  border-radius: 10px;
}
.custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background: #CBD5E1;
}

/* Animations */
@keyframes fadeInDown {
  from { opacity: 0; transform: translateY(-10px); }
  to { opacity: 1; transform: translateY(0); }
}

.animate-fade-in-down {
  animation: fadeInDown 0.5s cubic-bezier(0.16, 1, 0.3, 1) forwards;
}

@keyframes scaleIn {
  from { opacity: 0; transform: scale(0.9); }
  to { opacity: 1; transform: scale(1); }
}

.animate-scale-in {
  animation: scaleIn 0.3s cubic-bezier(0.16, 1, 0.3, 1);
}

.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s ease;
}
.fade-enter-from, .fade-leave-to {
  opacity: 0;
}
</style>